/**
 * Start from Photo — user uploads 1-5 photos, picks mode (single ad vs
 * multi-scene reel), and AI generates a ready-to-edit project.
 *
 * - 1 photo → defaults to "Single ad" (instagram_feed 1080×1080).
 * - 2-5 photos → forces "Storytelling reel" (instagram_reel 1080×1920).
 * - User can override mode with the toggle when exactly 1 photo is selected.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast, Toaster } from "sonner";
import {
  Upload, Sparkles, Loader2, X, Film, Image as ImageIcon,
  ChevronLeft, Layers, Zap,
} from "lucide-react";
import { StudioShell } from "../studio/StudioShell";
import { adsApiCall } from "./api";
import { useLanguage } from "../contexts/LanguageContext";

const MAX_PHOTOS = 5;

export default function PhotoStart() {
  const { t, lang } = useLanguage();
  const navigate = useNavigate();

  // Each local photo: { file, data_uri, asset_id?, uploading }
  const [photos, setPhotos] = useState([]);
  const [promptHint, setPromptHint] = useState("");
  const [mode, setMode] = useState("auto"); // "auto" | "single" | "reel" | "pack" | "epic"
  // Epic scene catalog + user picks
  const [sceneCatalog, setSceneCatalog] = useState([]);
  const [pickedScenes, setPickedScenes] = useState([]);
  useEffect(() => {
    adsApiCall.epicScenesCatalog()
      .then((r) => {
        setSceneCatalog(r.scenes || []);
        setPickedScenes(r.default_rotation || []);
      })
      .catch(() => { /* anon / 401 — catalog available only when logged in */ });
  }, []);
  const [generating, setGenerating] = useState(false);

  function onFiles(fileList) {
    const files = Array.from(fileList || []);
    const room = MAX_PHOTOS - photos.length;
    if (room <= 0) {
      toast.error(t.studio_photostart_max || `Max ${MAX_PHOTOS} photos`);
      return;
    }
    const accepted = files.slice(0, room).filter((f) => f.type?.startsWith("image/"));
    accepted.forEach((file) => {
      if (file.size > 20 * 1024 * 1024) {
        toast.error(`${file.name}: ${t.studio_photostart_too_large || "file too large (max 20MB)"}`);
        return;
      }
      const reader = new FileReader();
      reader.onload = (e) => {
        const localId = `local_${Math.random().toString(36).slice(2, 10)}`;
        const photo = {
          localId,
          file,
          data_uri: e.target.result,
          asset_id: null,
          uploading: true,
          error: false,
        };
        setPhotos((p) => [...p, photo]);
        // Upload to cloud immediately
        adsApiCall.uploadAsset(file)
          .then((rec) => setPhotos((prev) => prev.map((x) =>
            x.localId === localId ? { ...x, asset_id: rec.asset_id, uploading: false } : x
          )))
          .catch(() => setPhotos((prev) => prev.map((x) =>
            x.localId === localId ? { ...x, uploading: false, error: true } : x
          )));
      };
      reader.readAsDataURL(file);
    });
  }

  function removePhoto(localId) {
    setPhotos((p) => p.filter((x) => x.localId !== localId));
  }

  // Decide effective mode
  const effectiveMode = (() => {
    if (photos.length >= 2) return "reel";
    if (photos.length === 1 && mode === "reel") return "reel";
    if (photos.length === 1 && mode === "pack") return "pack";
    if (photos.length === 1 && mode === "epic") return "epic";
    return "single";
  })();

  async function generate() {
    if (!photos.length) {
      toast.error(t.studio_photostart_need_photo || "Add at least one photo first.");
      return;
    }
    if (photos.some((p) => p.uploading)) {
      toast.error(t.studio_photostart_wait_upload || "Please wait for uploads to finish.");
      return;
    }
    const okPhotos = photos.filter((p) => p.asset_id);
    if (!okPhotos.length) {
      toast.error(t.studio_photostart_upload_fail || "Upload failed, please retry.");
      return;
    }
    try {
      setGenerating(true);
      let project;
      if (effectiveMode === "epic") {
        if (!pickedScenes.length) {
          toast.error(t.studio_photostart_epic_need_scene || "Pick at least one epic scene.");
          setGenerating(false);
          return;
        }
        project = await adsApiCall.aiEpicSceneReel({
          asset_id: okPhotos[0].asset_id,
          prompt_hint: promptHint,
          language: lang || "en",
          scene_keys: pickedScenes.slice(0, 4),
        });
        toast.success(t.studio_photostart_epic_done
          || "Epic scenes ready — opening editor");
        navigate(`/ads-creator/editor/${project.project_id}`);
        return;
      }
      if (effectiveMode === "pack") {
        const pack = await adsApiCall.aiPhotoPack({
          asset_id: okPhotos[0].asset_id,
          prompt_hint: promptHint,
          language: lang || "en",
        });
        toast.success(t.studio_photostart_pack_done
          || `Created ${pack.count} ads — opening your library`);
        navigate(`/ads-creator/my-ads?pack=${pack.pack_id}`);
        return;
      }
      if (effectiveMode === "single") {
        project = await adsApiCall.aiPhotoDirector({
          asset_id: okPhotos[0].asset_id,
          prompt_hint: promptHint,
          language: lang || "en",
          format: "instagram_feed",
        });
      } else {
        project = await adsApiCall.aiPhotoReel({
          asset_ids: okPhotos.map((p) => p.asset_id),
          prompt_hint: promptHint,
          language: lang || "en",
          format: "instagram_reel",
        });
      }
      toast.success(t.studio_photostart_done || "Created — opening editor");
      navigate(`/ads-creator/editor/${project.project_id}`);
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || "Generation failed";
      toast.error(`${t.studio_photostart_fail || "Generation failed"}: ${msg}`);
    } finally {
      setGenerating(false);
    }
  }

  const uploadingCount = photos.filter((p) => p.uploading).length;
  const hasPhotos = photos.length > 0;

  return (
    <StudioShell activeKey="/studio/create">
      <Toaster position="top-center" theme="dark" />
      <div data-testid="photostart-page" style={{
        maxWidth: 940, margin: "0 auto", padding: "48px 20px 56px",
      }}>
        <button
          type="button"
          onClick={() => navigate("/studio/create")}
          style={{
            background: "transparent", border: 0, color: "#8b8ba7", fontSize: 13,
            cursor: "pointer", marginBottom: 18, display: "inline-flex", alignItems: "center", gap: 6,
          }}
          data-testid="photostart-back"
        >
          <ChevronLeft size={14} /> {t.studio_create_back_select || "Back"}
        </button>

        <div style={{
          display: "inline-flex", alignItems: "center", gap: 8,
          padding: "6px 14px", borderRadius: 999,
          background: "rgba(168,85,247,0.12)", border: "1px solid rgba(168,85,247,0.28)",
          color: "#c084fc", fontSize: 11, fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase",
          marginBottom: 16,
        }}>
          <Sparkles size={12} /> {t.studio_photostart_eyebrow || "Start from Photo"}
        </div>

        <h1 style={{
          color: "#fff", fontSize: "clamp(32px,4.6vw,52px)", fontWeight: 900,
          letterSpacing: "-0.02em", margin: "0 0 10px", lineHeight: 1.05,
        }}>
          {t.studio_photostart_title || "Upload your photo — AI writes the ad."}
        </h1>
        <p style={{ color: "#a5a5c0", fontSize: 16, margin: "0 0 28px", maxWidth: 600 }}>
          {t.studio_photostart_sub ||
            "Drop 1 photo for a single ad, or 2–5 photos for a storytelling reel. GPT-4o Vision reads the subject, mood, and palette — then writes copy that fits."}
        </p>

        {/* Upload zone */}
        <label
          data-testid="photostart-dropzone"
          htmlFor="photostart-file-input"
          onDragOver={(e) => { e.preventDefault(); e.currentTarget.style.borderColor = "#a855f7"; }}
          onDragLeave={(e) => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.14)"; }}
          onDrop={(e) => {
            e.preventDefault();
            e.currentTarget.style.borderColor = "rgba(255,255,255,0.14)";
            onFiles(e.dataTransfer.files);
          }}
          style={{
            display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
            gap: 10, padding: "36px 20px", textAlign: "center",
            background: "rgba(20,20,35,0.5)",
            border: "2px dashed rgba(255,255,255,0.14)",
            borderRadius: 18, color: "#e5e5f0", cursor: "pointer",
            transition: "border-color 160ms ease",
          }}
        >
          <div style={{
            width: 52, height: 52, borderRadius: 14,
            background: "linear-gradient(135deg,#a855f7,#7c3aed)",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <Upload size={22} color="#fff" />
          </div>
          <div style={{ fontSize: 16, fontWeight: 800 }}>
            {hasPhotos
              ? (t.studio_photostart_add_more || "Add another photo")
              : (t.studio_photostart_click_drop || "Click or drop photos here")}
          </div>
          <div style={{ fontSize: 12, color: "#8b8ba7" }}>
            PNG / JPG · {t.studio_photostart_max_hint || `up to ${MAX_PHOTOS} photos, 20 MB each`}
          </div>
          <input
            id="photostart-file-input"
            type="file"
            accept="image/*"
            multiple
            style={{ display: "none" }}
            data-testid="photostart-file-input"
            onChange={(e) => { onFiles(e.target.files); e.target.value = ""; }}
          />
        </label>

        {/* Thumbnails */}
        {hasPhotos && (
          <div
            data-testid="photostart-thumbs"
            style={{
              display: "grid", gap: 10, marginTop: 14,
              gridTemplateColumns: "repeat(auto-fill,minmax(120px,1fr))",
            }}
          >
            {photos.map((p, idx) => (
              <div key={p.localId} style={{
                position: "relative", aspectRatio: "1 / 1",
                borderRadius: 12, overflow: "hidden",
                border: p.error
                  ? "1px solid rgba(239,68,68,0.5)"
                  : "1px solid rgba(255,255,255,0.1)",
                background: "#111",
              }}>
                <img
                  src={p.data_uri}
                  alt=""
                  style={{ width: "100%", height: "100%", objectFit: "cover", opacity: p.uploading ? 0.6 : 1 }}
                  draggable={false}
                />
                <div style={{
                  position: "absolute", top: 6, left: 6,
                  padding: "2px 8px", borderRadius: 999,
                  background: "rgba(0,0,0,0.6)", color: "#fff",
                  fontSize: 11, fontWeight: 800,
                }}>
                  {idx + 1}
                </div>
                {p.uploading && (
                  <div style={{
                    position: "absolute", inset: 0, display: "flex",
                    alignItems: "center", justifyContent: "center",
                    background: "rgba(0,0,0,0.35)",
                  }}>
                    <Loader2 size={18} color="#fff" className="animate-spin" />
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => removePhoto(p.localId)}
                  aria-label="remove"
                  data-testid={`photostart-remove-${idx}`}
                  style={{
                    position: "absolute", top: 6, right: 6,
                    width: 24, height: 24, borderRadius: 999, border: 0,
                    background: "rgba(0,0,0,0.7)", color: "#fff", cursor: "pointer",
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}
                >
                  <X size={14} />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Mode toggle (only meaningful when 1 photo) */}
        {photos.length === 1 && (
          <div style={{ marginTop: 22, display: "flex", gap: 8, flexWrap: "wrap" }}>
            <ModeChip
              active={effectiveMode === "single"}
              onClick={() => setMode("single")}
              icon={<ImageIcon size={14} />}
              label={t.studio_photostart_mode_single || "Single ad · 1080×1080"}
              testid="photostart-mode-single"
            />
            <ModeChip
              active={effectiveMode === "reel"}
              onClick={() => setMode("reel")}
              icon={<Film size={14} />}
              label={t.studio_photostart_mode_reel || "Reel · 1080×1920 (single scene)"}
              testid="photostart-mode-reel"
            />
            <ModeChip
              active={effectiveMode === "pack"}
              onClick={() => setMode("pack")}
              icon={<Layers size={14} />}
              label={t.studio_photostart_mode_pack || "3-in-1 Pack · Feed + Story + Landscape"}
              testid="photostart-mode-pack"
              highlight
            />
            <ModeChip
              active={effectiveMode === "epic"}
              onClick={() => setMode("epic")}
              icon={<Zap size={14} />}
              label={t.studio_photostart_mode_epic || "Epic scenes · AI places you in Times Square, stadium, cinema"}
              testid="photostart-mode-epic"
              highlight
              badge="HOT"
            />
          </div>
        )}

        {/* Epic scene picker — appears only when Epic mode is chosen */}
        {effectiveMode === "epic" && sceneCatalog.length > 0 && (
          <div
            data-testid="photostart-epic-picker"
            style={{
              marginTop: 18,
              padding: 16,
              border: "1px solid rgba(168,85,247,0.3)",
              borderRadius: 14,
              background: "linear-gradient(135deg,rgba(168,85,247,0.08),rgba(236,72,153,0.05))",
            }}
          >
            <div style={{
              display: "flex", justifyContent: "space-between", alignItems: "baseline",
              marginBottom: 10, flexWrap: "wrap", gap: 8,
            }}>
              <div style={{ color: "#fff", fontSize: 13, fontWeight: 800 }}>
                {t.studio_photostart_epic_pick_title || "Pick up to 4 scenes"}
                <span style={{ color: "#c084fc", marginLeft: 8 }}>
                  ({pickedScenes.length}/4)
                </span>
              </div>
              <div style={{ color: "#8b8ba7", fontSize: 11 }}>
                {t.studio_photostart_epic_pick_hint || "1 credit per scene"}
              </div>
            </div>
            <div style={{
              display: "grid", gap: 8,
              gridTemplateColumns: "repeat(auto-fill,minmax(150px,1fr))",
            }}>
              {sceneCatalog.map((s) => {
                const isPicked = pickedScenes.includes(s.key);
                const atLimit = pickedScenes.length >= 4 && !isPicked;
                return (
                  <button
                    key={s.key}
                    type="button"
                    disabled={atLimit}
                    onClick={() => {
                      setPickedScenes((prev) =>
                        prev.includes(s.key)
                          ? prev.filter((k) => k !== s.key)
                          : prev.length < 4 ? [...prev, s.key] : prev
                      );
                    }}
                    data-testid={`photostart-epic-scene-${s.key}`}
                    style={{
                      textAlign: "left",
                      padding: "10px 12px",
                      borderRadius: 10,
                      border: isPicked
                        ? "1px solid #a855f7"
                        : "1px solid rgba(255,255,255,0.08)",
                      background: isPicked
                        ? "linear-gradient(135deg,rgba(168,85,247,0.2),rgba(124,58,237,0.18))"
                        : "rgba(20,20,35,0.6)",
                      color: "#fff",
                      cursor: atLimit ? "not-allowed" : "pointer",
                      opacity: atLimit ? 0.4 : 1,
                      position: "relative",
                      transition: "all 140ms ease",
                    }}
                  >
                    <div style={{
                      width: 24, height: 24, borderRadius: 6,
                      background: `linear-gradient(135deg,${s.palette?.primary || "#a855f7"},${s.palette?.secondary || "#7c3aed"})`,
                      marginBottom: 6,
                    }} />
                    <div style={{ fontSize: 12, fontWeight: 800, lineHeight: 1.2 }}>
                      {s.title}
                    </div>
                    {isPicked && (
                      <div style={{
                        position: "absolute", top: 6, right: 6,
                        width: 18, height: 18, borderRadius: "50%",
                        background: "#a855f7", color: "#fff",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: 11, fontWeight: 900,
                      }}>
                        {pickedScenes.indexOf(s.key) + 1}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}
        {photos.length >= 2 && (
          <div
            style={{ marginTop: 22, fontSize: 13, color: "#c084fc", fontWeight: 700 }}
            data-testid="photostart-mode-auto"
          >
            <Film size={14} style={{ verticalAlign: "middle", marginRight: 6 }} />
            {t.studio_photostart_auto_reel || `Multi-photo → storytelling reel (${photos.length} scenes)`}
          </div>
        )}

        {/* Prompt hint */}
        {hasPhotos && (
          <div style={{ marginTop: 22 }}>
            <div style={{ color: "#c6c6da", fontSize: 12, fontWeight: 700, marginBottom: 6 }}>
              {t.studio_photostart_hint_label || "One-sentence hint (optional)"}
            </div>
            <input
              value={promptHint}
              onChange={(e) => setPromptHint(e.target.value)}
              placeholder={t.studio_photostart_hint_ph || "e.g. Summer sale on handmade ceramic mugs"}
              data-testid="photostart-hint"
              style={{
                width: "100%", padding: "12px 14px",
                borderRadius: 10, fontSize: 14, color: "#fff",
                background: "rgba(20,20,35,0.7)",
                border: "1px solid rgba(255,255,255,0.1)",
                outline: "none",
              }}
            />
          </div>
        )}

        {/* Generate */}
        <button
          type="button"
          onClick={generate}
          disabled={!hasPhotos || generating || uploadingCount > 0}
          data-testid="photostart-generate"
          style={{
            marginTop: 22, width: "100%",
            padding: "14px 18px", borderRadius: 12, border: 0,
            color: "#fff", fontSize: 15, fontWeight: 800,
            cursor: (!hasPhotos || generating || uploadingCount > 0) ? "not-allowed" : "pointer",
            background: (!hasPhotos || generating || uploadingCount > 0)
              ? "rgba(80,60,120,0.5)"
              : "linear-gradient(135deg,#a855f7,#7c3aed)",
            display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8,
            transition: "transform 120ms ease",
          }}
        >
          {generating ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
          {generating
            ? (effectiveMode === "epic"
                ? (t.studio_photostart_generating_epic || "AI is placing you in epic scenes… (~40s)")
                : (t.studio_photostart_generating || "AI is writing your ad…"))
            : effectiveMode === "epic"
              ? (t.studio_photostart_btn_epic || "Generate epic scenes")
              : effectiveMode === "pack"
                ? (t.studio_photostart_btn_pack || "Generate 3-in-1 pack")
                : effectiveMode === "reel"
                  ? (t.studio_photostart_btn_reel || "Generate storytelling reel")
                  : (t.studio_photostart_btn_single || "Generate ad")}
        </button>

        <p style={{ color: "#8b8ba7", fontSize: 12, marginTop: 10, textAlign: "center" }}>
          {effectiveMode === "epic"
            ? ((t.studio_photostart_cost_epic_dyn || "{n} credits · AI places your subject into {n} epic scenes").replace(/\{n\}/g, pickedScenes.length || 3))
            : effectiveMode === "pack"
              ? (t.studio_photostart_cost_pack || "3 credits · 3 ads across Feed + Story + Landscape")
              : effectiveMode === "reel"
                ? (t.studio_photostart_cost_reel || `${photos.length} credit${photos.length === 1 ? "" : "s"} · opens in editor`)
                : (t.studio_photostart_cost_single || "1 credit · opens in editor")}
        </p>
      </div>
    </StudioShell>
  );
}

function ModeChip({ active, onClick, icon, label, testid, highlight, badge }) {
  const ring = highlight && !active
    ? "1px solid rgba(236,72,153,0.45)"
    : (active ? "1px solid transparent" : "1px solid rgba(255,255,255,0.1)");
  const showBadge = !active && (badge || highlight);
  const badgeText = badge || "NEW";
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      style={{
        display: "inline-flex", alignItems: "center", gap: 6,
        padding: "8px 14px", borderRadius: 999,
        fontSize: 12, fontWeight: 800, letterSpacing: "0.02em",
        color: active ? "#fff" : (highlight ? "#f9a8d4" : "#c6c6da"),
        background: active
          ? "linear-gradient(135deg,#a855f7,#7c3aed)"
          : "rgba(20,20,35,0.7)",
        border: ring,
        cursor: "pointer",
        position: "relative",
      }}
    >
      {icon} {label}
      {showBadge && (
        <span style={{
          position: "absolute", top: -7, right: -7,
          background: badgeText === "HOT" ? "#ef4444" : "#ec4899",
          color: "#fff", fontSize: 9,
          padding: "2px 6px", borderRadius: 999, fontWeight: 900,
          letterSpacing: "0.08em",
        }}>{badgeText}</span>
      )}
    </button>
  );
}
